import { Loader2, Lock, PenLine, Wallet } from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

import { partnerScheduleCashPriceRefusal } from './partnerSchedule';

interface GeneratePartnerScheduleDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** `BoardSlot.title` — the published title of the instrument being raised. */
    title: string;
    /** Present on a reissue, absent when this call is what generates the document. */
    referenceNo?: string | null;
    /**
     * `BoardSlot.cashPrice` — the CONTRACT's cash price, which `agreement_create` copies onto the
     * record when this is confirmed [D-B59].
     *
     * A DISPLAY AND NOT A FIELD. Nothing typed here reaches the server: the figure is read off the
     * contract at generation, so the only honest thing this dialog can do is show the one it will
     * state, and refuse where there is none.
     */
    cashPrice: number | null;
    isPending: boolean;
    onConfirm: () => void;
}

/**
 * Generate the Vehicle Schedule, stating the contract's Cash price.
 *
 * NOTHING ON THIS DIALOG IS TYPED [D-B59]. The schedule's figure is the contract's own cash price,
 * entered on Financials, and `agreement_create` copies it onto the record at the moment of
 * generation; the net return, the annualised return and the capital-recovery period are all divided
 * out of it. The dialog shows the figure it will state so the admin checks it before the partner
 * does, and holds Generate shut where the contract states none, which is the refusal the server
 * would raise (`AGREEMENT_CASH_PRICE_REQUIRED`).
 *
 * IT STAYS A DIALOG OF ITS OWN rather than a branch of `SendForSignatureDialog`, because it states a
 * figure the partner's returns are measured against and says where that figure is corrected.
 *
 * THE COPY IS TAKEN AT GENERATION, AND THE COPY SAYS SO. On a DRAFT contract a corrected cash price
 * withdraws this schedule and the next one states the new figure. Everything else on the schedule
 * follows the contract until the partner signs.
 *
 * THE SEQUENCE IS THE OTHER THING WORTH SAYING HERE. The partner signs first and Primelane
 * countersigns, exactly as on every other instrument, and until they have, no driver can sign the
 * hire purchase agreement on this vehicle.
 */
export default function GeneratePartnerScheduleDialog({
    open,
    onOpenChange,
    title,
    referenceNo,
    cashPrice,
    isPending,
    onConfirm,
}: GeneratePartnerScheduleDialogProps) {
    const refusal = partnerScheduleCashPriceRefusal(cashPrice);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* THE SHARED `DialogContent` SETS NO CEILING AND NO SCROLLER, and this is the tallest
                dialog on the board: header, the figure it will state, a hint that becomes the
                refusal where there is none, three bulleted paragraphs and a footer holding the
                irreversible Generate. On a short viewport the -50%/-50% centring pushes the
                overflow off both edges with no way to reach the buttons. Scoped here rather than
                changed on the primitive, which every dialog in the app renders through. */}
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
                <DialogHeader>
                    <DialogTitle>Generate the {title}</DialogTitle>
                    <DialogDescription>
                        {referenceNo
                            ? `${referenceNo} — the wording is generated from the current contract values.`
                            : 'The wording is generated from the current contract values when you confirm.'}
                    </DialogDescription>
                </DialogHeader>

                <dl className="space-y-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Cash price
                    </dt>
                    <dd
                        id="partner-schedule-cash-price"
                        aria-describedby="partner-schedule-cash-price-hint"
                        className={cn(
                            'text-lg font-semibold tabular-nums',
                            refusal && 'text-muted-foreground',
                        )}
                    >
                        {/* A null cash price always yields a refusal, so past it the figure is stated. */}
                        {refusal ? 'Not stated' : formatCurrency(cashPrice!)}
                    </dd>
                    {/* LIVE, SO THE REFUSAL IS HEARD. Where the contract states no cash price the
                        hint is replaced by the reason Generate is shut, and a screen reader that has
                        moved past the figure still hears why. */}
                    <dd
                        id="partner-schedule-cash-price-hint"
                        aria-live="polite"
                        className={cn('text-xs', refusal ? 'text-destructive' : 'text-muted-foreground')}
                    >
                        {refusal ??
                            'The contract’s cash price, from Financials. The schedule prints it as the ' +
                                'Cash price, and states the net return, the annualised return and the ' +
                                'capital-recovery period against it.'}
                    </dd>
                </dl>

                <ul className="space-y-3 border-t pt-4 text-sm">
                    <li className="flex gap-3">
                        <PenLine className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            The partner signs it in the Primelane partner app, and Primelane
                            countersigns here afterwards — the company always signs last.
                        </span>
                    </li>
                    <li className="flex gap-3">
                        <Wallet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            The Cash price is copied from Financials when this document is
                            generated. On a draft contract, correcting it there withdraws this
                            schedule; generate again afterwards.
                        </span>
                    </li>
                    <li className="flex gap-3">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <span>
                            No driver can sign the hire purchase agreement on this vehicle until the
                            partner has signed this.
                        </span>
                    </li>
                </ul>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={() => onConfirm()} disabled={isPending || refusal !== null}>
                        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Generate schedule
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
